import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"

/** The campaign detail drawer's open state lives here, so the drawer is linkable. */
export const CAMPAIGN_DRAWER_PARAM = "campaign"

/**
 * URL-only state for the campaign detail drawer, following the same single-source-of-truth
 * approach as the booking step's `useBookingParams`: the URL is the state, there is no Redux or
 * `useState` mirror to keep in sync.
 *
 * History model — PUSH on open, REPLACE on close. `useBookingParams` replaces throughout; this
 * differs on open so the back button is a working "close". The asymmetry is the point:
 *
 * - Open from the list, press Back → pops the pushed entry, drawer closes, list remains.
 * - Close with X, press Back → the close REPLACED the drawer entry with the plain list, so Back
 *   goes to whatever preceded the list. Pushing here instead would make Back reopen the campaign
 *   the user just deliberately closed.
 * - Deep link straight to `?campaign=x`, then close → replace turns that single entry into the
 *   plain list, so Back doesn't assume a list entry that was never visited.
 */
export function useCampaignDrawerParam() {
  const [searchParams, setSearchParams] = useSearchParams()

  const campaignId = searchParams.get(CAMPAIGN_DRAWER_PARAM)

  const openCampaign = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(CAMPAIGN_DRAWER_PARAM, id)
          return next
        },
        { replace: false },
      )
    },
    [setSearchParams],
  )

  const closeCampaign = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete(CAMPAIGN_DRAWER_PARAM)
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])

  return { campaignId, openCampaign, closeCampaign }
}
